import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { 
  Train, 
  Clock, 
  Star, 
  Leaf, 
  PoundSterling,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  Shield,
  FileCheck,
  ChevronRight,
  Loader2,
  MapPin,
  Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Score color helper
const getScoreColor = (score) => {
  if (score >= 75) return 'text-emerald-400';
  if (score >= 60) return 'text-amber-400';
  return 'text-red-400';
};

const getScoreBg = (score) => {
  if (score >= 75) return 'bg-emerald-500/20 border-emerald-500/30';
  if (score >= 60) return 'bg-amber-500/20 border-amber-500/30';
  return 'bg-red-500/20 border-red-500/30';
};

// Trend icon helper
const TrendIcon = ({ trend }) => {
  if (trend === 'improving') return <TrendingUp className="w-3 h-3 text-emerald-400" />;
  if (trend === 'declining') return <TrendingDown className="w-3 h-3 text-red-400" />;
  return <Minus className="w-3 h-3 text-slate-400" />;
};

// Journey Card Component
const JourneyCard = ({ journey, isSelected, onSelect, onMakeDecision }) => {
  const { evidence } = journey;
  
  return (
    <Card 
      data-testid={`journey-card-${journey.id}`}
      className={`bg-slate-800/50 border transition-all cursor-pointer hover:bg-slate-800/70 ${
        isSelected ? 'border-cyan-500 ring-2 ring-cyan-500/30' : 'border-slate-700'
      }`}
      onClick={() => onSelect(journey.id)}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Train className="w-5 h-5 text-cyan-400" />
              <span className="font-semibold text-white">{journey.operator}</span>
            </div>
            <div className="text-sm text-slate-400 mt-1">
              {journey.origin} → {journey.destination}
            </div>
          </div>
          <div className={`px-3 py-1.5 rounded-lg border ${getScoreBg(journey.evidence_score)}`}>
            <span className={`text-lg font-bold ${getScoreColor(journey.evidence_score)}`}>
              {journey.evidence_score}
            </span>
            <span className="text-xs text-slate-400 ml-1">/100</span>
          </div>
        </div>
        
        {/* Time and Price */}
        <div className="flex justify-between items-center mb-4 pb-4 border-b border-slate-700">
          <div className="flex items-center gap-4">
            <div>
              <div className="text-xl font-bold text-white">{journey.departure_time}</div>
              <div className="text-xs text-slate-500">Depart</div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-500" />
            <div>
              <div className="text-xl font-bold text-white">{journey.arrival_time}</div>
              <div className="text-xs text-slate-500">Arrive</div>
            </div>
            <div className="text-slate-400 text-sm ml-2">
              ({journey.duration_formatted})
            </div>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold text-emerald-400">£{journey.price_gbp.toFixed(2)}</div>
            <div className="text-xs text-slate-500">Standard</div>
          </div>
        </div>
        
        {/* Evidence Metrics */}
        {evidence && (
          <div className="grid grid-cols-3 gap-3 mb-4">
            {/* Punctuality */}
            <div className="bg-slate-900/50 rounded-lg p-3">
              <div className="flex items-center gap-1 mb-1">
                <Clock className="w-3 h-3 text-blue-400" />
                <span className="text-xs text-slate-400">Punctuality</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-lg font-bold text-white">
                  {evidence.punctuality.on_time_percentage}%
                </span>
                <TrendIcon trend={evidence.punctuality.trend} />
              </div>
              <div className="text-xs text-slate-500">
                Avg {evidence.punctuality.average_delay_minutes}m delay
              </div>
            </div>
            
            {/* Satisfaction */}
            <div className="bg-slate-900/50 rounded-lg p-3">
              <div className="flex items-center gap-1 mb-1">
                <Star className="w-3 h-3 text-amber-400" />
                <span className="text-xs text-slate-400">Satisfaction</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-lg font-bold text-white">
                  {evidence.satisfaction.overall_rating}
                </span>
                <span className="text-xs text-slate-500">/5</span>
              </div>
              <div className="text-xs text-slate-500">
                {evidence.satisfaction.total_reviews.toLocaleString()} reviews
              </div>
            </div>
            
            {/* Environmental */}
            <div className="bg-slate-900/50 rounded-lg p-3">
              <div className="flex items-center gap-1 mb-1">
                <Leaf className="w-3 h-3 text-emerald-400" />
                <span className="text-xs text-slate-400">Eco Rating</span>
              </div>
              <div className="text-lg font-bold text-white">
                {evidence.environmental.environmental_rating}
              </div>
              <div className="text-xs text-slate-500">
                {evidence.environmental.co2_grams_per_passenger_km}g CO₂/km
              </div>
            </div>
          </div>
        )}
        
        {/* Themes */}
        {evidence && (
          <div className="flex flex-wrap gap-1 mb-4">
            {evidence.satisfaction.positive_themes.slice(0, 2).map((theme) => (
              <Badge key={`pos-${theme}`} variant="outline" className="text-xs bg-emerald-500/10 border-emerald-500/30 text-emerald-400">
                {theme.replace(/_/g, ' ')}
              </Badge>
            ))}
            {evidence.satisfaction.negative_themes.slice(0, 1).map((theme) => (
              <Badge key={`neg-${theme}`} variant="outline" className="text-xs bg-red-500/10 border-red-500/30 text-red-400">
                {theme.replace(/_/g, ' ')}
              </Badge>
            ))}
          </div>
        )}
        
        {/* Select Button */}
        {isSelected && (
          <Button 
            data-testid={`select-journey-${journey.id}`}
            className="w-full bg-cyan-600 hover:bg-cyan-700"
            onClick={(e) => {
              e.stopPropagation();
              onMakeDecision(journey);
            }}
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Book This Journey with Proven Evidence
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Decision Receipt Component
const DecisionReceipt = ({ receipt, onClose }) => {
  const journey = receipt.journey_selected;
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <Card className="bg-slate-900 border-slate-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-emerald-500/20">
              <FileCheck className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <CardTitle className="text-white">Decision Confirmed</CardTitle>
              <CardDescription>Your booking decision has been recorded with proven evidence</CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          {/* Journey Summary */}
          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-slate-400 mb-3">Journey Booked</h3>
            <div className="flex items-center gap-4">
              <Train className="w-8 h-8 text-cyan-400" />
              <div className="flex-1">
                <div className="font-semibold text-white">{journey.operator}</div>
                <div className="text-sm text-slate-400">
                  {journey.origin} → {journey.destination}
                </div>
                <div className="text-sm text-slate-400">
                  {journey.departure_time} - {journey.arrival_time} ({journey.duration_formatted})
                </div>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-emerald-400">£{journey.price_gbp.toFixed(2)}</div>
                <div className="text-xs text-slate-500">{journey.travel_date}</div>
              </div>
            </div>
          </div>
          
          {/* Evidence Summary */}
          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-slate-400 mb-3">Evidence Used</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-slate-500">Punctuality</div>
                <div className="text-lg font-bold text-white">
                  {receipt.evidence_summary.punctuality?.on_time_percentage}% on-time
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Satisfaction</div>
                <div className="text-lg font-bold text-white">
                  {receipt.evidence_summary.satisfaction?.overall_rating}/5 rating
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Composite Score</div>
                <div className={`text-lg font-bold ${getScoreColor(receipt.evidence_summary.composite_score)}`}>
                  {receipt.evidence_summary.composite_score}/100
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Reviews Considered</div>
                <div className="text-lg font-bold text-white">
                  {receipt.evidence_summary.satisfaction?.total_reviews?.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
          
          {/* Katlas Provenance */}
          <div className={`rounded-lg p-4 ${receipt.katlas_signed ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
            <div className="flex items-center gap-3 mb-3">
              <Shield className={`w-5 h-5 ${receipt.katlas_signed ? 'text-emerald-400' : 'text-amber-400'}`} />
              <h3 className="font-medium text-white">
                {receipt.katlas_signed ? 'Cryptographically Signed' : 'Signature Pending'}
              </h3>
            </div>
            
            {receipt.katlas_signed && receipt.katlas_receipt && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Receipt Hash:</span>
                  <code className="text-cyan-400 font-mono text-xs">
                    {receipt.katlas_receipt.receipt_hash?.substring(0, 24)}...
                  </code>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Signer:</span>
                  <code className="text-cyan-400 font-mono text-xs">
                    {receipt.katlas_receipt.signer_fingerprint?.substring(0, 16)}...
                  </code>
                </div>
              </div>
            )}
            
            <div className="mt-3 text-xs text-slate-400">
              Evidence Hash: <code className="text-slate-500">{receipt.evidence_hash}</code>
            </div>
          </div>
          
          {/* Provenance Chain */}
          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-slate-400 mb-3">Provenance Chain</h3>
            <div className="space-y-2">
              {receipt.provenance_chain.map((step) => (
                <div key={`step-${step.step}-${step.action}`} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center text-xs text-cyan-400 font-bold">
                    {step.step}
                  </div>
                  <div className="text-sm text-slate-300">{step.action.replace(/_/g, ' ')}</div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Rationale */}
          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-slate-400 mb-2">Decision Rationale</h3>
            <p className="text-slate-300 text-sm">{receipt.decision_rationale}</p>
          </div>
          
          {/* Confidence Badge */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">Confidence Level:</span>
              <Badge className={
                receipt.confidence_level === 'high' ? 'bg-emerald-500' :
                receipt.confidence_level === 'medium' ? 'bg-amber-500' : 'bg-red-500'
              }>
                {receipt.confidence_level.toUpperCase()}
              </Badge>
            </div>
            <div className="text-xs text-slate-500">
              Receipt ID: {receipt.receipt_id}
            </div>
          </div>
          
          <Button 
            data-testid="close-receipt-btn"
            className="w-full bg-slate-700 hover:bg-slate-600" 
            onClick={onClose}
          >
            Close
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Component
const PassengerDecisionDemo = () => {
  const [searchParams, setSearchParams] = useState({
    origin: 'London',
    destination: 'Manchester',
    travel_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    time_preference: 'afternoon'
  });
  
  const [searchResult, setSearchResult] = useState(null);
  const [selectedJourneyId, setSelectedJourneyId] = useState(null);
  const [decisionReceipt, setDecisionReceipt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSearchResult(null);
    setSelectedJourneyId(null);
    
    try {
      const response = await fetch(`${API_URL}/api/passenger/journeys/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(searchParams)
      });
      
      if (!response.ok) throw new Error('Search failed');
      
      const data = await response.json();
      setSearchResult(data);
      
      // Auto-select the best option
      if (data.journeys.length > 0) {
        setSelectedJourneyId(data.journeys[0].id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);
  
  const handleMakeDecision = useCallback(async (journey) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/passenger/decision/make`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          search_id: searchResult.search_id,
          journey_id: journey.id,
          passenger_name: 'Demo Passenger',
          decision_criteria: ['punctuality', 'satisfaction']
        })
      });
      
      if (!response.ok) throw new Error('Decision failed');
      
      const receipt = await response.json();
      setDecisionReceipt(receipt);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [searchResult]);
  
  return (
    <div data-testid="passenger-decision-demo" className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Train className="w-10 h-10 text-cyan-400" />
            <h1 className="text-3xl font-bold text-white">Passenger Decision</h1>
          </div>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Compare train journeys with proven evidence. Make informed decisions backed by 
            real punctuality data, customer satisfaction, and environmental impact - 
            cryptographically signed for provenance.
          </p>
        </div>
        
        {/* Search Form */}
        <Card className="bg-slate-800/50 border-slate-700 mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              <div>
                <Label className="text-slate-400 mb-2 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> From
                </Label>
                <Input
                  data-testid="origin-input"
                  value={searchParams.origin}
                  onChange={(e) => setSearchParams(p => ({ ...p, origin: e.target.value }))}
                  placeholder="London"
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-400 mb-2 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> To
                </Label>
                <Input
                  data-testid="destination-input"
                  value={searchParams.destination}
                  onChange={(e) => setSearchParams(p => ({ ...p, destination: e.target.value }))}
                  placeholder="Manchester"
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-400 mb-2 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Date
                </Label>
                <Input
                  data-testid="date-input"
                  type="date"
                  value={searchParams.travel_date}
                  onChange={(e) => setSearchParams(p => ({ ...p, travel_date: e.target.value }))}
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-400 mb-2 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Time
                </Label>
                <select
                  data-testid="time-select"
                  value={searchParams.time_preference}
                  onChange={(e) => setSearchParams(p => ({ ...p, time_preference: e.target.value }))}
                  className="w-full h-10 rounded-md bg-slate-900 border border-slate-600 text-white px-3"
                >
                  <option value="morning">Morning</option>
                  <option value="afternoon">Afternoon</option>
                  <option value="evening">Evening</option>
                  <option value="any">Any Time</option>
                </select>
              </div>
              <Button 
                data-testid="search-btn"
                onClick={handleSearch}
                disabled={loading}
                className="bg-cyan-600 hover:bg-cyan-700 h-10"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Search Journeys'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </div>
        )}
        
        {/* Results */}
        {searchResult && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">
                {searchResult.total_options} journeys found
              </h2>
              <div className="text-sm text-slate-400">
                Sorted by evidence score (best first)
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {searchResult.journeys.map((journey) => (
                <JourneyCard
                  key={journey.id}
                  journey={journey}
                  isSelected={selectedJourneyId === journey.id}
                  onSelect={setSelectedJourneyId}
                  onMakeDecision={handleMakeDecision}
                />
              ))}
            </div>
            
            {/* Data Source Info */}
            <div className="mt-6 text-center text-sm text-slate-500">
              Data source: {searchResult.data_source_info?.name} ({searchResult.data_source_info?.type})
              <br />
              <span className="text-xs">{searchResult.data_source_info?.disclaimer}</span>
            </div>
          </div>
        )}
        
        {/* Empty State */}
        {!searchResult && !loading && (
          <div className="text-center py-16">
            <Train className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-400 mb-2">Ready to search</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              Enter your journey details above to compare train options with evidence-based metrics.
            </p>
          </div>
        )}
        
        {/* Decision Receipt Modal */}
        {decisionReceipt && (
          <DecisionReceipt 
            receipt={decisionReceipt} 
            onClose={() => setDecisionReceipt(null)}
          />
        )}
      </div>
    </div>
  );
};

export default PassengerDecisionDemo;
